import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, DownloadSimple, Paperclip, CalendarCheck, PencilSimpleLine, Tray, Phone } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { fetchPdfBlob } from "@/lib/printPdf";
import { KIND, DIRECTION, TONE, Pill, fmtDateTime, counterpart } from "./commMeta";
import CommunicationDetail from "./CommunicationDetail";
import NoteDialog from "./NoteDialog";
import IncomingDialog from "./IncomingDialog";
import OwnerLetterDialog from "./OwnerLetterDialog";

function Row({ e, onOpen }) {
  const K = KIND[e.kind] || KIND.note;
  const D = DIRECTION[e.direction] || DIRECTION.internal;
  return (
    <button type="button" onClick={() => onOpen(e.id)} data-testid={`comm-row-${e.id}`}
      className="w-full text-left grid grid-cols-[92px_1fr] sm:grid-cols-[120px_1fr_auto] gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors">
      <div className="text-[11px] text-slate-500 tabular-nums">{fmtDateTime(e.occurred_at)}</div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Pill cls={D.cls}><D.icon size={10} /> {D.label}</Pill>
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"><K.icon size={12} /> {K.label}</span>
          {e.batch_mode && <Pill cls="text-violet-700 bg-violet-50 border-violet-200">{e.batch_mode === "serial" ? "Serienbrief" : "Rundschreiben"}</Pill>}
          {e.follow_up?.status === "open" && <Pill cls="text-amber-800 bg-amber-50 border-amber-200" testId={`comm-row-followup-${e.id}`}><CalendarCheck size={10} /> WV {e.follow_up.due_date}</Pill>}
          {e.voided && <Pill cls="text-rose-700 bg-rose-50 border-rose-200" testId={`comm-row-voided-${e.id}`}>storniert / gegenstandslos</Pill>}
          {e.reply_ids?.length > 0 && <Pill cls="text-emerald-700 bg-emerald-50 border-emerald-200">beantwortet</Pill>}
        </div>
        <div className="text-sm font-medium text-slate-900 truncate mt-0.5">{e.subject}</div>
        <div className="text-[11px] text-slate-500 truncate">
          {e.direction === "in" ? "Von" : "An"}: {counterpart(e)} · {e.author_name}
          {e.attachments?.length > 0 && <span className="ml-1 inline-flex items-center gap-0.5"><Paperclip size={10} />{e.attachments.length}</span>}
        </div>
      </div>
      <div className="hidden sm:block self-center"><Pill cls={TONE[e.status?.tone] || TONE.neutral}>{e.status?.label}</Pill></div>
    </button>
  );
}

// Reiter „Kommunikation“ — scope: { property_id | tenancy_id | contact_id | vorgang_id }
export default function CommunicationTab({ scope, ownerTarget = null, title = "" }) {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, open_follow_ups: 0 });
  const [f, setF] = useState({ q: "", kind: "all", direction: "all", date_from: "", date_to: "", follow_up: false });
  const [openId, setOpenId] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [inOpen, setInOpen] = useState(false);
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const scopeKey = JSON.stringify(scope);
  const params = useMemo(() => ({
    ...JSON.parse(scopeKey), q: f.q || undefined, kind: f.kind !== "all" ? f.kind : undefined,
    direction: f.direction !== "all" ? f.direction : undefined, date_from: f.date_from || undefined,
    date_to: f.date_to || undefined, follow_up: f.follow_up ? "open" : undefined,
  }), [scopeKey, f]);

  const load = useCallback(() => {
    api.get("/communications", { params: { ...params, limit: 300 } })
      .then(({ data }) => { setItems(data.items || []); setMeta({ total: data.total, open_follow_ups: data.open_follow_ups }); })
      .catch(() => toast.error("Kommunikation konnte nicht geladen werden"));
  }, [params]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    const h = () => setTimeout(load, 800);
    window.addEventListener("domivus:message-sent", h);
    return () => window.removeEventListener("domivus:message-sent", h);
  }, [load]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const exportAs = async (fmt) => {
    const qs = new URLSearchParams(Object.entries({ ...params, title }).filter(([, v]) => v)).toString();
    try {
      const { blob } = await fetchPdfBlob(`/communications/export.${fmt}?${qs}`);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Kommunikation${title ? ` ${title}` : ""}.${fmt}`;
      a.click();
    } catch (e) { toast.error(e.message); }
  };

  const replyLetter = (e) => {
    const context = { ...JSON.parse(scopeKey), in_reply_to_id: e.id };
    const cid = e.direction === "in" ? e.sender?.contact_id : e.recipients?.[0]?.contact_id;
    if (cid) context.contact_id = cid;
    if (context.tenancy_id && cid) { context.journal_tenancy_id = context.tenancy_id; delete context.tenancy_id; }
    window.dispatchEvent(new CustomEvent("domivus:open-composer", { detail: { context } }));
  };

  return (
    <div className="space-y-3" data-testid="communication-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm text-slate-600 mr-auto" data-testid="comm-count">
          {meta.total} Einträge{meta.open_follow_ups ? ` · ${meta.open_follow_ups} offene Wiedervorlage${meta.open_follow_ups === 1 ? "" : "n"}` : ""}
        </div>
        {ownerTarget && <Button size="sm" className="gap-1 bg-slate-900 hover:bg-slate-800" onClick={() => setOwnerOpen(true)} data-testid="comm-owner-letter-btn"><PencilSimpleLine size={14} /> An Eigentümer schreiben</Button>}
        <Button size="sm" variant="outline" className="gap-1" onClick={() => { setReplyTo(null); setInOpen(true); }} data-testid="comm-incoming-btn"><Tray size={14} /> Eingang erfassen</Button>
        <Button size="sm" variant="outline" className="gap-1" onClick={() => { setReplyTo(null); setNoteOpen(true); }} data-testid="comm-note-btn"><Phone size={14} /> Gesprächsnotiz</Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button size="sm" variant="outline" className="gap-1" data-testid="comm-export-btn"><DownloadSimple size={14} /> Export</Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => exportAs("pdf")} data-testid="comm-export-pdf">PDF-Dokumentation</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => exportAs("zip")} data-testid="comm-export-zip">ZIP (Originale + CSV)</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Betreff, Inhalt, Empfänger…" className="pl-8 h-9" data-testid="comm-search" />
        </div>
        <Select value={f.kind} onValueChange={(v) => set("kind", v)}>
          <SelectTrigger className="w-32 h-9" data-testid="comm-filter-kind"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Alle Arten</SelectItem>{Object.entries(KIND).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.direction} onValueChange={(v) => set("direction", v)}>
          <SelectTrigger className="w-32 h-9" data-testid="comm-filter-direction"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Ein & Aus</SelectItem>{Object.entries(DIRECTION).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} className="w-36 h-9" data-testid="comm-filter-from" />
        <Input type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} className="w-36 h-9" data-testid="comm-filter-to" />
        <Button size="sm" variant={f.follow_up ? "default" : "outline"} className="h-9" onClick={() => set("follow_up", !f.follow_up)} data-testid="comm-filter-followup">Offene Wiedervorlagen</Button>
      </div>
      <div className="border border-slate-200 rounded-md divide-y divide-slate-100 bg-white">
        {items.length === 0 && <div className="p-6 text-sm text-slate-500 text-center" data-testid="comm-empty">Noch keine Kommunikation dokumentiert.</div>}
        {items.map((e) => <Row key={e.id} e={e} onOpen={setOpenId} />)}
      </div>
      <CommunicationDetail id={openId} onClose={() => setOpenId(null)} onChanged={load}
        onReplyIncoming={(e) => { setReplyTo(e); setInOpen(true); }} onReplyLetter={replyLetter} />
      <NoteDialog open={noteOpen} onOpenChange={setNoteOpen} scope={JSON.parse(scopeKey)} inReplyTo={replyTo} onSaved={load} />
      <IncomingDialog open={inOpen} onOpenChange={setInOpen} scope={JSON.parse(scopeKey)} inReplyTo={replyTo} onSaved={() => { load(); setOpenId(null); }} />
      {ownerTarget && <OwnerLetterDialog open={ownerOpen} onOpenChange={setOwnerOpen} target={ownerTarget} />}
    </div>
  );
}
